// client/src/pages/admin/ManageHome.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';

export default function ManageHome() {
  const token = localStorage.getItem('authToken');

  const [config, setConfig] = useState(null);

  // Basic hero text fields
  const [heroTitle, setHeroTitle] = useState('');
  const [heroSubtitle, setHeroSubtitle] = useState('');
  // Colors for hero text
  const [heroTitleColor, setHeroTitleColor] = useState('#ffffff');
  const [heroSubtitleColor, setHeroSubtitleColor] = useState('#ffffff');

  // **New**: hero button text and color
  const [heroButtonText, setHeroButtonText] = useState('');
  const [heroButtonColor, setHeroButtonColor] = useState('#ffffff');

  // Featured arrays
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);

  // File uploads
  const [logoFile, setLogoFile] = useState(null);
  const [heroImageFile, setHeroImageFile] = useState(null);

  // We also store all products/blogs
  const [allProducts, setAllProducts] = useState([]);
  const [allBlogs, setAllBlogs] = useState([]);

  useEffect(() => {
    fetchHomeConfig();
    fetchProductsAndBlogs();
    // eslint-disable-next-line
  }, []);

  async function fetchHomeConfig() {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/home/home-config`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setConfig(data);

      setHeroTitle(data.heroTitle || '');
      setHeroSubtitle(data.heroSubtitle || '');
      setHeroTitleColor(data.heroTitleColor || '#ffffff');
      setHeroSubtitleColor(data.heroSubtitleColor || '#ffffff');

      // new fields
      setHeroButtonText(data.heroButtonText || 'Explore Products');
      setHeroButtonColor(data.heroButtonColor || '#ffffff');

      setFeaturedProducts(data.featuredProducts?.map(p => p._id) || []);
      setFeaturedBlogs(data.featuredBlogs?.map(b => b._id) || []);
    } catch (err) {
      console.error('Error fetching home-config:', err);
      alert('Failed to load home config');
    }
  }

  async function fetchProductsAndBlogs() {
    try {
      const [prodRes, blogRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/products`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/blogs`)
      ]);
      setAllProducts(prodRes.data);
      setAllBlogs(blogRes.data);
    } catch (err) {
      console.error('Error fetching products/blogs:', err);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('heroTitle', heroTitle);
      formData.append('heroSubtitle', heroSubtitle);
      formData.append('heroTitleColor', heroTitleColor);
      formData.append('heroSubtitleColor', heroSubtitleColor);

      // new fields for hero button
      formData.append('heroButtonText', heroButtonText);
      formData.append('heroButtonColor', heroButtonColor);

      featuredProducts.forEach(id => formData.append('featuredProducts', id));
      featuredBlogs.forEach(id => formData.append('featuredBlogs', id));

      if (logoFile) {
        formData.append('logo', logoFile);
      }
      if (heroImageFile) {
        formData.append('heroImage', heroImageFile);
      }

      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/home/home-config`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      alert('Home config updated successfully!');
      fetchHomeConfig(); // refresh
    } catch (err) {
      console.error('Error updating home config:', err);
      alert('Failed to update home config');
    }
  }

  function toggleFeaturedProduct(productId) {
    setFeaturedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  }

  function toggleFeaturedBlog(blogId) {
    setFeaturedBlogs(prev => {
      if (prev.includes(blogId)) {
        return prev.filter(id => id !== blogId);
      } else {
        return [...prev, blogId];
      }
    });
  }

  if (!config) {
    return <div className="p-4">Loading home config...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Manage Home Page</h1>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        
        {/* Logo */}
        <div>
          <label className="block font-medium mb-1">Current Logo</label>
          {config.logo && (
            <img
              src={`${process.env.REACT_APP_API_URL}/${config.logo}`}
              alt="Logo"
              className="h-16 mb-2"
            />
          )}
          <input
            type="file"
            accept="image/*"
            onChange={e => setLogoFile(e.target.files[0])}
          />
        </div>

        {/* Hero Image */}
        <div>
          <label className="block font-medium mb-1">Hero Image</label>
          {config.heroImage && (
            <img
              src={`${process.env.REACT_APP_API_URL}/${config.heroImage}`}
              alt="Hero"
              className="h-32 mb-2 object-cover"
            />
          )}
          <input
            type="file"
            accept="image/*"
            onChange={e => setHeroImageFile(e.target.files[0])}
          />
        </div>

        {/* Hero Title */}
        <div>
          <label className="block font-medium mb-1">Hero Title</label>
          <input
            type="text"
            className="border p-2 w-full"
            value={heroTitle}
            onChange={e => setHeroTitle(e.target.value)}
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Hero Title Color</label>
          <input
            type="color"
            className="border p-2 w-16 h-10"
            value={heroTitleColor}
            onChange={e => setHeroTitleColor(e.target.value)}
          />
        </div>

        {/* Hero Subtitle */}
        <div>
          <label className="block font-medium mb-1">Hero Subtitle</label>
          <input
            type="text"
            className="border p-2 w-full"
            value={heroSubtitle}
            onChange={e => setHeroSubtitle(e.target.value)}
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Hero Subtitle Color</label>
          <input
            type="color"
            className="border p-2 w-16 h-10"
            value={heroSubtitleColor}
            onChange={e => setHeroSubtitleColor(e.target.value)}
          />
        </div>

        {/* Hero Button Text + Color */}
        <div>
          <label className="block font-medium mb-1">Hero Button Text</label>
          <input
            type="text"
            className="border p-2 w-full"
            value={heroButtonText}
            onChange={e => setHeroButtonText(e.target.value)}
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Hero Button Color</label>
          <input
            type="color"
            className="border p-2 w-16 h-10"
            value={heroButtonColor}
            onChange={e => setHeroButtonColor(e.target.value)}
          />
        </div>

        {/* Featured Products */}
        <div>
          <h3 className="font-semibold mb-2">Featured Products</h3>
          <p className="text-sm text-gray-600 mb-2">Click to toggle selection</p>
          <div className="flex flex-wrap gap-2">
            {allProducts.map(prod => (
              <button
                key={prod._id}
                type="button"
                onClick={() => toggleFeaturedProduct(prod._id)}
                className={`px-3 py-1 rounded border 
                  ${featuredProducts.includes(prod._id)
                    ? 'bg-green-500 text-white border-green-500'
                    : 'bg-gray-100 border-gray-300 text-gray-700'}
                `}
              >
                {prod.name}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Blogs */}
        <div>
          <h3 className="font-semibold mb-2">Featured Blogs</h3>
          <p className="text-sm text-gray-600 mb-2">Click to toggle selection</p>
          <div className="flex flex-wrap gap-2">
            {allBlogs.map(blog => (
              <button
                key={blog._id}
                type="button"
                onClick={() => toggleFeaturedBlog(blog._id)}
                className={`px-3 py-1 rounded border 
                  ${featuredBlogs.includes(blog._id)
                    ? 'bg-green-500 text-white border-green-500'
                    : 'bg-gray-100 border-gray-300 text-gray-700'}
                `}
              >
                {blog.title}
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="bg-primaryGreen text-white px-4 py-2 rounded hover:bg-green-600"
        >
          Save Changes
        </button>
      </form>
    </div>
  );
}
